import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, AccountAlias, Assignment, Party } from '../api';
import { SectionCard } from '../ui/components';
import { ActionQueueCard } from '../ui/mobileOps';
import {
  Button,
  Checkbox,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

// Rates are stored as decimal multipliers (1 = 100%); the form takes percents.
function pctToRate(pct: string): string {
  const n = Number(pct);
  if (!Number.isFinite(n)) return pct.trim();
  return String(+(n / 100).toFixed(8));
}

export function StepAccounts() {
  const qc = useQueryClient();

  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });
  const clients = useQuery<Party[]>({
    queryKey: ['parties', 'client'],
    queryFn: () => api.get<Party[]>('/api/parties?role=client'),
  });
  const accounts = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });

  return (
    <div className="space-y-4">
      <ActionQueueCard
        section="accounts"
        eyebrow="Step 4"
        title="Create each account with its first alias and assignment"
        description="This single step wires the account label you use internally, the raw name you expect to see in the imported sheet, and who currently holds the account."
        meta={
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span>Uplines ready</span>
              <span className="font-medium text-ink">{uplines.data?.length ?? 0}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Clients ready</span>
              <span className="font-medium text-ink">{clients.data?.length ?? 0}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Accounts created</span>
              <span className="font-medium text-ink">{accounts.data?.length ?? 0}</span>
            </div>
          </div>
        }
      />

      <AddAccountForm uplines={uplines.data ?? []} clients={clients.data ?? []} />

      <SectionCard section="accounts" title="Accounts so far">
        {accounts.isLoading ? (
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Spinner /> loading...
          </div>
        ) : accounts.data && accounts.data.length > 0 ? (
          <div className="space-y-3">
            {accounts.data.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                clients={clients.data ?? []}
                onChange={() => void qc.invalidateQueries()}
              />
            ))}
          </div>
        ) : (
          <p className="text-caption text-ink-muted">No accounts yet.</p>
        )}
      </SectionCard>
    </div>
  );
}

function AddAccountForm({ uplines, clients }: { uplines: Party[]; clients: Party[] }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    uplinePartyId: '',
    canonicalName: '',
    panel: '',
    creditLimitLabel: '',
    currencyLabel: 'INR',
    rawName: '',
    clientPartyId: '',
    selfHeld: false,
    clientRate: '',
    uplineRate: '',
    defaultPointsSplit: false,
    effectiveFrom: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: async () => {
      if (!form.uplinePartyId) throw new Error('Pick an upline.');
      if (!form.canonicalName.trim()) throw new Error('Canonical account name is required.');
      if (!form.rawName.trim()) throw new Error('First alias (raw upline name) is required.');
      if (!form.uplineRate.trim()) throw new Error('Upline rate is required.');
      if (!form.selfHeld && !form.clientPartyId) throw new Error('Pick a client or mark self-held.');
      if (!form.selfHeld && !form.clientRate.trim()) throw new Error('Client rate is required.');
      if (!form.effectiveFrom) throw new Error('Effective-from date is required.');
      const account = await api.post<Account>('/api/accounts', {
        uplinePartyId: Number(form.uplinePartyId),
        canonicalName: form.canonicalName.trim(),
        panel: form.panel.trim() || null,
        creditLimitLabel: form.creditLimitLabel.trim() || null,
        currencyLabel: form.currencyLabel.trim() || 'INR',
        // One-time setup often adds a run of similar names (leo1, leo11…);
        // skip the Phase-4c similar-name prompt here to keep setup smooth.
        allowSimilar: true,
      });
      await api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, { rawName: form.rawName.trim() });
      await api.post<Assignment>('/api/assignments', {
        accountId: account.id,
        clientPartyId: form.selfHeld ? null : Number(form.clientPartyId),
        clientRate: form.selfHeld ? null : pctToRate(form.clientRate),
        uplineRate: pctToRate(form.uplineRate),
        defaultPointsSplit: form.defaultPointsSplit,
        effectiveFrom: form.effectiveFrom,
      });
      return account;
    },
    onSuccess: () => {
      setForm((prev) => ({
        ...prev,
        canonicalName: '',
        panel: '',
        creditLimitLabel: '',
        rawName: '',
        clientRate: '',
        uplineRate: '',
      }));
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  return (
    <SectionCard section="accounts" title="Add an account">
      <p className="mb-4 text-[13px] leading-5 text-ink-muted">
        This creates the account, saves the first raw alias exactly as it appears on the upline sheet, and opens the first assignment in one pass.
      </p>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          createMut.mutate();
        }}
      >
        <Select
          label="Upline"
          value={form.uplinePartyId}
          onChange={(value) => setForm({ ...form, uplinePartyId: value })}
          options={uplines.map((upline) => ({ value: String(upline.id), label: upline.displayName }))}
          placeholder="Choose..."
        />
        <TextInput
          label="Canonical name"
          value={form.canonicalName}
          onChange={(e) => setForm({ ...form, canonicalName: e.target.value })}
          placeholder="e.g. Lotus365Aura"
          hint="Internal label shown throughout the app."
        />
        <TextInput
          label="Panel"
          value={form.panel}
          onChange={(e) => setForm({ ...form, panel: e.target.value })}
          placeholder="e.g. SAURABJI-CLICK"
        />
        <TextInput
          label="Credit limit label"
          value={form.creditLimitLabel}
          onChange={(e) => setForm({ ...form, creditLimitLabel: e.target.value })}
          placeholder="e.g. 50 KG or 2.2 CR"
        />
        <TextInput
          label="First alias (raw sheet name)"
          value={form.rawName}
          onChange={(e) => setForm({ ...form, rawName: e.target.value })}
          placeholder="e.g. S ADMIN (LOTUS365AURA)"
          hint="Case is preserved. Matching still stays case-insensitive."
        />
        <TextInput
          label="Currency label"
          value={form.currencyLabel}
          onChange={(e) => setForm({ ...form, currencyLabel: e.target.value })}
        />

        <div className="col-span-full rounded-[22px] border border-border/70 bg-surface-raised p-3">
          <div className="mb-2 text-small font-semibold tracking-[-0.01em] text-ink">Assignment</div>
          <Checkbox
            label="Self-held"
            hint="Turn this on if the account belongs to you directly, with no client and no client rate."
            checked={form.selfHeld}
            onChange={(value) => setForm({ ...form, selfHeld: value })}
          />
        </div>

        {!form.selfHeld && (
          <Select
            label="Client"
            value={form.clientPartyId}
            onChange={(value) => setForm({ ...form, clientPartyId: value })}
            options={clients.map((client) => ({ value: String(client.id), label: client.displayName }))}
            placeholder="Choose..."
          />
        )}
        {!form.selfHeld && (
          <TextInput
            label="Client rate (%)"
            value={form.clientRate}
            onChange={(e) => setForm({ ...form, clientRate: e.target.value })}
            placeholder="14"
            hint="Percent, e.g. 14 — negatives allowed"
            inputMode="text"
          />
        )}
        <TextInput
          label="Upline rate (%)"
          value={form.uplineRate}
          onChange={(e) => setForm({ ...form, uplineRate: e.target.value })}
          placeholder="6"
          hint="Percent, e.g. 6 — negatives allowed"
          inputMode="text"
        />
        <TextInput
          label="Effective from"
          type="date"
          value={form.effectiveFrom}
          onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
        />
        <div className="col-span-full">
          <Checkbox
            label="Split client points by default"
            hint="Leave this off for most accounts. Turn it on only when the client and upline points routinely differ."
            checked={form.defaultPointsSplit}
            onChange={(value) => setForm({ ...form, defaultPointsSplit: value })}
          />
        </div>

        {error && (
          <div className="col-span-full">
            <ErrorBanner>{error}</ErrorBanner>
          </div>
        )}

        <div className="col-span-full flex items-center gap-2 pt-1">
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? <Spinner /> : 'Add account'}
          </Button>
        </div>
      </form>
    </SectionCard>
  );
}

function AccountRow({
  account,
  clients,
  onChange,
}: {
  account: Account;
  clients: Party[];
  onChange: () => void;
}) {
  const aliases = useQuery<AccountAlias[]>({
    queryKey: ['aliases', account.id],
    queryFn: () => api.get<AccountAlias[]>(`/api/accounts/${account.id}/aliases`),
  });
  const assignments = useQuery<Assignment[]>({
    queryKey: ['assignments', account.id],
    queryFn: () => api.get<Assignment[]>(`/api/assignments?account=${account.id}`),
  });

  const addAlias = useMutation({
    mutationFn: (rawName: string) => api.post<AccountAlias>(`/api/accounts/${account.id}/aliases`, { rawName }),
    onSuccess: () => {
      onChange();
    },
  });
  const [newAlias, setNewAlias] = useState('');

  return (
    <div className="rounded-[24px] border border-border/70 bg-surface p-4 shadow-sm backdrop-blur">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-small font-semibold tracking-[-0.01em] text-ink">{account.canonicalName}</div>
            <Pill tone={account.status === 'active' ? 'green' : 'amber'}>{account.status}</Pill>
          </div>
          <div className="mt-1 text-[12px] leading-5 text-ink-muted">
            {[account.panel, account.creditLimitLabel ? `credit ${account.creditLimitLabel}` : null].filter(Boolean).join(' · ') || 'No panel or credit label'}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-[20px] bg-surface-raised/70 p-3 ring-1 ring-border/50">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">Aliases</div>
          <div className="mt-2 space-y-1 text-caption text-ink-muted">
            {aliases.data?.length ? aliases.data.map((alias) => <div key={alias.id}>{alias.rawName}</div>) : <div>Loading aliases...</div>}
          </div>
          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const name = newAlias.trim();
              if (!name) return;
              addAlias.mutate(name);
              setNewAlias('');
            }}
          >
            <input
              className="min-h-[40px] flex-1 rounded-[14px] border border-border/70 bg-surface px-3 py-2 text-small text-ink shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
              placeholder="add another raw alias"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
            />
            <Button type="submit" variant="secondary" size="sm" disabled={addAlias.isPending}>
              {addAlias.isPending ? <Spinner /> : 'Add'}
            </Button>
          </form>
        </div>

        <div className="rounded-[20px] bg-surface-raised/70 p-3 ring-1 ring-border/50">
          <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted">Assignments</div>
          <div className="mt-2 space-y-2 text-caption text-ink-muted">
            {assignments.data?.length ? (
              assignments.data.map((assignment) => {
                const clientName = assignment.clientPartyId === null
                  ? 'Self'
                  : clients.find((client) => client.id === assignment.clientPartyId)?.displayName ?? `#${assignment.clientPartyId}`;
                return (
                  <div key={assignment.id} className="rounded-[16px] bg-surface-raised px-3 py-2 ring-1 ring-border/40">
                    <div className="font-medium text-ink">{clientName}</div>
                    <div className="mt-1">upline {Number(assignment.uplineRate) * 100}%</div>
                    {assignment.clientRate && <div>client {Number(assignment.clientRate) * 100}%</div>}
                    <div>{assignment.effectiveFrom} to {assignment.effectiveTo ?? 'open'}</div>
                  </div>
                );
              })
            ) : (
              <div>Loading assignments...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
